import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState(null); // null | 'loading' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  if (!token) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Érvénytelen vagy hiányzó token. Kérjük, kérjen új jelszó-visszaállítási linket.
          </Alert>
          <Button component={Link} to="/forgot-password" variant="contained">
            Új link kérése
          </Button>
        </Paper>
      </Container>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!newPassword) {
      setStatus('error');
      setErrorMessage('Kérjük, adjon meg új jelszót.');
      return;
    }
    if (newPassword.length < 6) {
      setStatus('error');
      setErrorMessage('A jelszónak legalább 6 karakter hosszúnak kell lennie.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setStatus('error');
      setErrorMessage('A két jelszó nem egyezik.');
      return;
    }

    setStatus('loading');
    try {
      await apiService.post(API_ENDPOINTS.AUTH.RESET_PASSWORD, { token, newPassword });
      navigate('/login', {
        state: { message: 'Jelszava sikeresen megváltozott. Most már bejelentkezhet.' },
      });
    } catch (err) {
      setStatus('error');
      const msg = err?.response?.data?.message || err?.message || '';
      if (msg.toLowerCase().includes('lejárt') || msg.toLowerCase().includes('érvénytelen') || msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('expired')) {
        setErrorMessage('A link érvénytelen vagy lejárt. Kérjük, kérjen új jelszó-visszaállítási linket.');
      } else {
        setErrorMessage('Hiba történt a jelszó módosítása során. Kérjük, próbálja újra.');
      }
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          Új jelszó beállítása
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Adja meg az új jelszavát.
        </Typography>

        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStatus(null)}>
            {errorMessage}
            {errorMessage.includes('lejárt') || errorMessage.includes('érvénytelen') ? (
              <Box sx={{ mt: 1 }}>
                <Button component={Link} to="/forgot-password" size="small" variant="outlined">
                  Új link kérése
                </Button>
              </Box>
            ) : null}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Új jelszó"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={status === 'loading'}
            required
            helperText="Minimum 6 karakter"
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((p) => !p)} edge="end" tabIndex={-1}>
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Jelszó megerősítése"
            type={showPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={status === 'loading'}
            required
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={status === 'loading'}
            sx={{ py: 1.5, mb: 2 }}
          >
            {status === 'loading' ? <CircularProgress size={24} color="inherit" /> : 'Jelszó mentése'}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Button component={Link} to="/login" variant="text" size="small">
            Vissza a bejelentkezéshez
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPassword;
